<template>
  <div>
    <img
      :src="slideActive.image.large"
      alt="question-image"
      class="slide-img"
      v-if="presentation.audiencePacing && slideActive.image"
    >
    <swiper
      ref="canvasHintsSwiper"
      :options="swiperOptions"
      v-if="!presentation.audiencePacing"
    >
      <swiper-slide :class="{'w-full': isNoHintsVisible}" v-if="slideActive.image">
        <div
          class="slide-img-wrapper --has-question-image"
        >
          <!-- This acts as a frame for responsive question image -->
          <img
            src="@/assets/images/hints/canvas-hint-background.svg"
            alt="hints"
            v-if="hasClassBackgroundForHints"
            class="hints__image"
          >
          <img
            :src="slideActive.image.large"
            alt="question-image"
            class="slide-img"
            :class="{'bg-base-0 absolute': hasClassBackgroundForHints,
                     'hints-showing': !isNoHintsVisible && !slideActive.backgroundImage,
            }"
          >
        </div>
      </swiper-slide>
      <swiper-slide
        v-if="isDefaultHintImageVisible"
        class="w-full"
        :class="{'custom-d-none': isDefaultHintImageHidden}"
      >
        <div class="slide-img-wrapper">
          <img
            src="@/assets/images/hints/default.svg"
            alt="default-hints-image"
            class="slide-img"
          >
        </div>
      </swiper-slide>
      <swiper-slide
        v-for="(hint, index) in visibleHints"
        :key="index"
        :class="{'custom-d-none': hintsClosed,
                 'w-full': slideActive.numberOfHintsShown === 1 && !slideActive.image}"
      >
        <img
          src="@/assets/images/hints/canvas-hint-background.svg"
          alt="hints"
          class="hints__image"
        >
        <p class="hints__text" v-if="hint.title">
          {{ hint.title || '' }}
        </p>
      </swiper-slide>
      <button
        slot="pagination"
        variant="link"
        class="custom-text-sm font-bold swiper-btn-pagination swiper-button-prev"
        v-if="presentation.audiencePacing"
      >
        <div class="prev-button__background" />
      </button>
      <button
        slot="pagination"
        variant="link"
        class="custom-text-sm font-bold swiper-btn-pagination swiper-button-next"
        v-if="presentation.audiencePacing"
      >
        <div class="next-button__background" />
      </button>
      <div
        class="bottom-0 w-full swiper-pagination"
        :class="{'custom-d-none': isNoHintsVisible}"
        slot="pagination"
      />
    </swiper>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { Swiper, SwiperSlide, directive } from 'vue-awesome-swiper'
import { Pagination } from 'swiper'
import 'swiper/css/swiper.css'
import EventBus from '@/eventBus.js'

export default {
  components: {
    Swiper,
    SwiperSlide,
  },
  directives: {
    swiper: directive,
  },
  setup() {
    return {
      modules: [Pagination],
    }
  },
  data() {
    return {
      swiperOptions: {
        speed: 300,
        slidesPerView: 'auto',
        spaceBetween: 30,
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
          bulletClass: 'swiper-custom-bullet',
          bulletActiveClass: 'swiper-custom-bullet-active',
          renderBullet(index, className) {
            return `<span class="${className}">•</span>`
          },
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
      },
      visibleHints: [],
      hintsClosed: false,
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
    }),
    ...mapGetters({
      slideActive: 'activeActivity',
    }),
    swiper() {
      return this.$refs.canvasHintsSwiper ? this.$refs.canvasHintsSwiper.swiperInstance : null
    },
    isPickAnswerAndPickImage() {
      return this.slideActive.type === 'pickAnswer' && (!this.slideActive.slideType || this.slideActive.slideType === 'imageChoice')
    },
    hasHints() {
      return this.slideActive.hints && this.slideActive.hints.length &&
        this.isPickAnswerAndPickImage
    },
    isNoHintsVisible() {
      return !this.visibleHints || !this.visibleHints.length ||
        (this.visibleHints && this.visibleHints.length && this.hintsClosed)
    },
    isDefaultHintImageVisible() {
      return this.hasHints && !this.slideActive.image && this.slideActive.isHintsVisible
    },
    isDarkTheme() {
      // white text
      return this.slideActive.textColour === '#ffffff'
    },
    hasClassHintsShowingDark() {
      return !this.isNoHintsVisible && this.slideActive.backgroundImage && this.isDarkTheme
    },
    hasClassHintsShowingLight() {
      return !this.isNoHintsVisible && this.slideActive.backgroundImage && !this.isDarkTheme
    },
    isDefaultHintImageHidden() {
      return (this.visibleHints && this.visibleHints.length &&
        this.visibleHints.some((hint) => hint.show)) && !this.hintsClosed
    },
    hasClassBackgroundForHints() {
      return this.isPickAnswerAndPickImage && this.slideActive.isHintsVisible
    },
  },
  watch: {
    isDefaultHintImageHidden() {
      if (this.presentation.audiencePacing) return
      this.swiper.allowTouchMove = this.isDefaultHintImageHidden
    },
  },
  mounted() {
    if (this.swiper && this.isDefaultHintImageHidden === 0 && !this.presentation.audiencePacing) {
      this.swiper.allowTouchMove = false
    }
    this._handleShowHints(this.slideActive.numberOfHintsShown, this.slideActive.hintsShowingIndex)
    EventBus.$on('hints: update-show-hints', (data) => {
      this._handleShowHints(data.numberOfHintsShown, data.hintsShowingIndex)
    })
  },
  destroyed() {
    EventBus.$off('hints: update-show-hints')
  },
  methods: {
    _handleShowHints(numberOfHintsShown, hintsShowingIndex) {
      if (!this.hasHints) return
      const noHintsEverShown = !numberOfHintsShown && numberOfHintsShown !== 0
      const isHintsClosed = !hintsShowingIndex && hintsShowingIndex !== 0

      // if presenter has shown all 3 hints -> audience F5 -> has all 3 hints shown
      if (noHintsEverShown) {
        this.visibleHints = []
      } else {
        for (let i = 0; i <= numberOfHintsShown - 1; i++) {
          this.visibleHints.push(this.slideActive.hints[i])
          this.visibleHints = this.visibleHints.filter((hint) => hint.id)
          this.visibleHints = [...new Set(this.visibleHints)]
        }
      }

      // if presenter show next hint -> audience show next hint
      if (isHintsClosed) {
        this.hintsClosed = true
      } else {
        this._handlePresenterNextBackHints(hintsShowingIndex)
      }
    },
    _handlePresenterNextBackHints(hintsShowingIndex) {
      this.hintsClosed = false
      for (let i = 0; i <= hintsShowingIndex; i++) {
        if (!this.slideActive.hints[i].show) {
          this.slideActive.hints[i].show = true
        }
      }
      this.slideActive.hints.push()
      setTimeout(() => {
        if (!this.swiper) return
        this.swiper.slideTo(hintsShowingIndex + 1, 300, false) // next in 300ms
      }, 200)
    },
  },
}
</script>

<style lang="scss" scoped>
.slide-img-wrapper {
  width: auto;
  margin: 0 auto;
  overflow: hidden;
  padding-bottom: 0;
  display: block;
  &.--has-question-image {
    position: relative;
    margin-bottom: 16px;
    .slide-img {
      top: 0;
      max-height: 100%;
    }
  }
}
.slide-img {
  margin-bottom: 16px;
  max-height: 600px;
  &.bg-base-0 {
    background-color: #ffffff;
  }
}
.swiper-slide {
  position: relative;
  width: 90%;
  &.w-full {
    width: 100%;
  }
}
.hints__image {
  max-height: 600px;
  width: 100%;
}
.hints__text {
  position: absolute;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  margin-bottom: 0;
  margin-top: 0;
  font-size: 1.9em;
  line-height: 1.4;
  font-weight: bold;
  color: #313131;
  padding: 2rem;
  word-break: break-word;
  @media (max-width: 480px) {
    font-size: 1.1em;
    line-height: 1.2;
    padding:0.75rem;
  }
}
.swiper-pagination {
  position: unset;
  &::v-deep {
    .swiper-custom-bullet {
      color: #CECECE;
      cursor: pointer;
      font-size: 20px;
      margin: 0 1px;
      &.swiper-custom-bullet-active {
        color: #348EF6;
      }
    }

  }
}
</style>
